import { useState } from 'react';
import {
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  TextField,
} from '@mui/material';
import axios from 'axios';

import config from '../config/config.json';
import { CadencePlayer, PlayerCadenceResult } from '../types';
import { LoadingButton } from '@mui/lab';

type PlayerCadenceFormProps = {
  readonly onDraw: (result: PlayerCadenceResult) => void;
};

export default function PlayersCadenceForm({ onDraw }: PlayerCadenceFormProps) {
  const [fetching, setFetching] = useState(false);
  const [players, setPlayers] = useState<string>('');
  const [numWeeks, setNumWeeks] = useState<number>(3);
  const [error, setError] = useState<string | null>(null);

  const handlePlayersChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPlayers(event.target.value);
  };

  const fetchPlayers = () => {
    setFetching(true);
    setError(null);
    axios
      .get(config.webAppURL, {
        params: {
          playersCadence: true,
          numWeeks,
        },
      })
      .then((resp) => {
        if (resp.data.error) {
          setError(resp.data.error);
          return;
        }
        const dates: string[] = resp.data.dates;
        const cadencePlayers: CadencePlayer[] = [];
        const inputPlayers: string[] = players.split('\n').map((p) => p.trim());
        const fetchedPlayers: Record<string, number[]> = resp.data.players;

        Object.entries(fetchedPlayers).forEach(([player, weeks]) => {
          if (inputPlayers.includes(player)) {
            cadencePlayers.push({
              name: player,
              cadence: weeks,
              appearances: weeks.reduce((acc, week) => acc + week, 0),
            });
          }
        });

        onDraw({ dates, players: cadencePlayers });
      })
      .catch((e) => {
        console.error(e);
        setError('Error fetching players');
      })
      .finally(() => {
        setFetching(false);
      });
  };

  const isValidForm = players.trim().length > 0;

  return (
    <Stack direction="column">
      <Typography variant="body2" sx={{ mb: 2 }}>
        Check cadence of players to see who play in what week
      </Typography>
      {error && (
        <Typography variant="body2" color="error">
          {error}
        </Typography>
      )}
      <form>
        <Stack direction="column" spacing={2}>
          <TextField
            id="players"
            name="players"
            label="Players List"
            multiline
            maxRows={28}
            fullWidth
            value={players}
            onChange={handlePlayersChange}
            sx={{ mb: 2, flexGrow: 1 }}
            disabled={fetching}
          />
          <FormControl sx={{ flexGrow: 1 }}>
            <InputLabel>Number of weeks</InputLabel>
            <Select
              id="numWeeks"
              name="numWeeks"
              label="Number of weeks"
              value={numWeeks}
              onChange={(event) =>
                setNumWeeks(parseInt(event.target.value as string))
              }
              disabled={fetching}
            >
              <MenuItem value={2}>2</MenuItem>
              <MenuItem value={3}>3</MenuItem>
              <MenuItem value={4}>4</MenuItem>
            </Select>
          </FormControl>
          <LoadingButton
            variant="contained"
            type="submit"
            fullWidth
            loading={fetching}
            disabled={fetching || !isValidForm}
            onClick={fetchPlayers}
          >
            Show
          </LoadingButton>
        </Stack>
      </form>
    </Stack>
  );
}
